/**
 * The contract between a flood solver and whatever drives it.
 *
 * Deliberately a standalone module with no implementation and no rendering
 * import. It is what lets `donor-cell-solver-v1.ts` and `flood-renderer.ts`
 * stay mutually unaware: the controller imports both, each imports only this
 * (and the shared field layer), and neither imports the other.
 *
 * UNITS, once, for the whole interface:
 *   lengths, depths, elevations  metres
 *   times                        seconds
 *   elevations are relative to the scene's local origin
 */
import type * as THREE from 'three/webgpu'

/**
 * Forcing applied by ONE numerical step.
 *
 * UNITS ARE DEPTH, NOT RATE — established by inspecting the implementation,
 * not assumed. The kernel adds these straight to the depth field with no dt
 * factor of its own (`newDepth = max(0, h + dh + rainfall - infiltration)`),
 * and every call site pre-integrates a rate into a depth before passing it
 * (production: `rate * (substepDt / 60)` for rain, `ratePerSecond * substepDt`
 * for infiltration). The names encode that so a future solver author cannot
 * accidentally apply dt twice or omit it.
 */
export interface FloodForcing {
  /** Rain depth added by this step, metres. ALREADY integrated over dtSeconds. */
  rainfallDepthM: number
  /** Infiltration depth removed by this step, metres. ALREADY integrated over dtSeconds. */
  infiltrationDepthM: number
}

/**
 * The GPU-resident fields a solver publishes.
 *
 * PRESENCE IS THE CAPABILITY: a field is offered iff it is defined. A consumer
 * asks `if (solver.outputs.maxDepth)`, never `instanceof SomeSolverV2` and
 * never a solver-name string. A separate parallel `capabilities` flag map was
 * considered and rejected — two structures describing the same thing can
 * desync, and a flag claiming a field that isn't there is worse than no flag.
 *
 * These are SEMANTIC fields, not a dump of a solver's internal textures.
 * Scratch state (flux working buffers, momentum accumulators, integration
 * temporaries) stays private even when a derived field is published — "a GPU
 * texture exists" and "a public output" are different things.
 *
 * Nothing here carries visual styling. No palette, legend, opacity, colour
 * ramp or display range: those belong to whatever eventually renders these,
 * not to a numerical contract.
 *
 * ---------------------------------------------------------------------------
 * CURRENT CAPABILITIES, BY SOLVER
 * ---------------------------------------------------------------------------
 *   V1 Donor Cell               depth only, always — never reads `analysis`
 *   V2 Conservative Two-Pass    depth always; maxDepth/arrivalTime/duration/flux
 *                               each independently optional (see
 *                               `FloodAnalysisOptions`), fixed at construction
 *   Local-Inertial / Dynamic-Wave (not yet implemented)
 *                              depth, physically meaningful momentum/velocity,
 *                              temporal analysis
 *
 * ---------------------------------------------------------------------------
 * WHY THERE IS NO `velocity` FIELD HERE
 * ---------------------------------------------------------------------------
 * The current donor-cell scheme exchanges water between cells according to a
 * surface-gradient rule. That inter-cell transfer is NOT physical velocity,
 * and a future conservative CA's face transfers will not be either. Publishing
 * a `velocity` hook now would invite hazard formulas (`depth * velocity`) built
 * on a quantity the solver never computed. A velocity field appears here only
 * when a solver genuinely integrates momentum.
 *
 * ---------------------------------------------------------------------------
 * ANALYSIS USES PHYSICAL STATE, NEVER RENDER STATE
 * ---------------------------------------------------------------------------
 * Every field here is physical solver output. Engineering analysis must never
 * be derived from `visualDryOffsetM`, the surface bias, the AA band or the
 * render mask threshold — those exist to make water look right on screen and
 * have no physical meaning. Notably a future `maxDepth` is
 * `max(previousMaxDepth, physicalDepth)`, NOT max of `signedVisualDepth`.
 *
 * All times are SIMULATION time, never wall-clock.
 */
export interface FloodSolverOutputs {
  /**
   * Current water depth, metres. MANDATORY — every solver publishes it, and it
   * is what the water renderer consumes.
   *
   * Must always reflect the CURRENT ping-pong resource. Implementations must
   * expose it as a live accessor, not a value captured at construction:
   * capturing would freeze it to the A texture and render stale water on every
   * other step.
   */
  readonly depth: THREE.Texture

  /**
   * Directional outgoing volumetric discharge per cell, m³/s. Deliberately
   * called flux, not velocity — see the note above; V2's model is a
   * non-inertial diffusive CA and never integrates momentum.
   *
   * Present only on `ConservativeTwoPassSolverV2` and only when
   * `analysis.flux` is enabled. RGBA32Float, one outgoing face per channel —
   * `r`=North, `g`=East, `b`=South, `a`=West — cell-centred, outward-only,
   * non-negative. A shared edge's net flow is the caller's to combine from the
   * two neighbouring cells' relevant channels; boundary-facing channels are
   * exactly 0.
   *
   * Reflects the FINAL physical substep of the most recent `advance()` call —
   * not time-averaged, not an accumulated volume — the same "as of the last
   * `advance()`" semantics `depthTexture` itself documents.
   *
   * `Q_f = storedTransferTerm · conductance` is independent of `dt` ONLY while
   * the solver's internal donor-global limiter is inactive for that donor. When
   * the limiter is active, `Q_f` is still the correct, physically real capped
   * discharge for the `dt` that substep actually used — just not portable to a
   * different `dt` choice. Both are the true dimensional meaning of the
   * current transport equations, not a defect in either regime.
   *
   * Stale across `reset()` until the next `advance()` re-seeds it to zero: it
   * has no "initial condition" the way depth does, since it means nothing until
   * transport has actually run once.
   */
  readonly flux?: THREE.Texture

  /**
   * Greatest physical depth reached at each cell so far, metres.
   * `max(previousMaxDepth, physicalDepth)`, evaluated against the FINAL
   * post-transport/rainfall/infiltration depth each physical substep — physical
   * depth, never visual.
   *
   * Present only on `ConservativeTwoPassSolverV2` and only when
   * `analysis.maxDepth` is enabled. Seeded from the solver's initial depth
   * field (not zero), so water already present at construction/`reset()`
   * counts as an observed maximum from t = 0 — a field that only ever recedes
   * still reports the depth it started at, never a lower number.
   */
  readonly maxDepth?: THREE.Texture

  /**
   * Simulation seconds — elapsed on the solver's own clock since construction
   * or the last `reset()`, never wall-clock — at which each cell's depth FIRST
   * reached the shared wet threshold ({@link FloodAnalysisOptions.wetThresholdM}).
   *
   * Present only on `ConservativeTwoPassSolverV2` and only when
   * `analysis.arrivalTime` is enabled. `-1` where the cell has not yet crossed
   * the threshold — zero is unavailable for that purpose because arrival at
   * t = 0 (a cell already wet at construction/`reset()`) is itself a real,
   * distinct value the seed writes explicitly.
   *
   * Crossing is END-OF-SUBSTEP and discrete: the timestamp recorded is
   * `fp32(elapsedSecondsBeforeThisSubstep + dtSeconds)`, checked against the
   * FINAL post-substep depth for that physical substep, inclusive (`depth >=
   * threshold`). No within-step interpolation is attempted; the resulting
   * uncertainty is bounded by one physical substep. Once set, a value never
   * changes — this is FIRST arrival, not most recent.
   */
  readonly arrivalTime?: THREE.Texture

  /**
   * Accumulated simulation seconds each cell spent at or above the shared wet
   * threshold ({@link FloodAnalysisOptions.wetThresholdM}) — never derived from
   * `depth > 0` (floating-point residue would count as flooding) and never
   * from the renderer's shoreline/AA thresholds, which are visual.
   *
   * Present only on `ConservativeTwoPassSolverV2` and only when
   * `analysis.duration` is enabled. LEFT-ENDPOINT OCCUPANCY: for the interval
   * `[t, t + dt)`, the interval's full `dt` is credited iff the cell's depth
   * was AT OR ABOVE the threshold at the START of that interval — the
   * PRE-substep depth, not the post-substep one. This is what keeps `duration`
   * coherent with `arrivalTime`'s own end-of-substep timestamp: a cell first
   * crossing during substep k is credited nothing for the interval that
   * precedes its own recorded arrival, and everything from the next interval
   * onward provided it stays wet at each interval's start. A cell wet at an
   * interval's start and dry by its end still receives that interval's full
   * `dt` — deliberate discrete integration, with no within-step interpolation
   * in this model.
   */
  readonly duration?: THREE.Texture
}

/**
 * Default physical wet threshold for the analysis outputs, metres.
 *
 * An ENGINEERING parameter, deliberately not any of the visual constants: not
 * `EDGE_ANTIALIAS_BAND_M` (antialiasing band), not the renderer's
 * `visualDryOffsetM` (0.02, render mask). Those exist to make water look right
 * and carry no physical meaning. It lives here, next to the output contract it
 * defines, rather than beside the visual constants it must never be confused
 * with.
 *
 * 0.01 m matches the threshold the physics validation harness has always used
 * for its own wet/dry metrics, so analysis and validation agree on what "wet"
 * means.
 */
export const DEFAULT_ANALYSIS_WET_THRESHOLD_M = 0.01

/**
 * Which analysis outputs a solver should compute, chosen at CONSTRUCTION and
 * fixed for the instance's lifetime.
 *
 * Immutable by design, not by omission. `maxDepth`, `arrivalTime` and
 * `duration` are HISTORICAL: they summarise everything that has happened since
 * the run began. Enabling one midway through a run could not reconstruct the
 * history it missed, so a half-populated field would silently misreport. The
 * only honest options are "on from the start" or "absent", and absence is
 * itself the capability signal — `outputs` simply has no such key.
 *
 * Every flag defaults to false, and a solver constructed without this option
 * allocates no analysis textures, builds no analysis kernels, creates no
 * analysis uniforms and issues no extra dispatches.
 */
export interface FloodAnalysisOptions {
  /** Greatest physical depth seen per cell. Seeded from the initial depth field, so t=0 water counts. */
  maxDepth?: boolean
  /** Simulation seconds at which each cell first became wet; `-1` where still dry. */
  arrivalTime?: boolean
  /** Accumulated simulation seconds each cell spent wet. */
  duration?: boolean
  /** Directional outgoing discharge per face. */
  flux?: boolean
  /**
   * Physical wet threshold in metres, shared by `arrivalTime` and `duration`
   * so the two can never disagree about what "wet" means. Ignored by
   * `maxDepth` and `flux`, which need no threshold.
   *
   * Must be finite and strictly positive. Zero is rejected rather than
   * clamped: the comparison is inclusive (`depth >= threshold`), so a zero
   * threshold would classify genuinely dry cells as wet and make both outputs
   * meaningless. Defaults to {@link DEFAULT_ANALYSIS_WET_THRESHOLD_M}.
   */
  wetThresholdM?: number
}

/**
 * What a flood solver presents to its controller.
 *
 * The controller asks one thing — "advance the simulation" — and reads one
 * thing — "the current depth field". It never learns how many compute passes a
 * step costs, which ping-pong resource is live, or how any renderer is bound.
 * That is what allows `DonorCellSolverV1` (one pass), a future conservative
 * two-pass solver (flux pass + mass pass), or a dynamic-wave solver (N passes)
 * to be swapped underneath without touching the controller or the renderer.
 *
 * Note there is no `terrainTexture` here: visible terrain is a RENDERING
 * input, not numerical solver state, and exposing it from the solver would be
 * an ownership leak. `bedTexture` IS exposed, because the water surface is
 * reconstructed against the hydraulic floor the solver integrates over, so the
 * renderer legitimately needs the same field the physics used.
 */
export interface FloodSolver {
  /**
   * Advances the simulation by `substeps` numerical steps of `dtSeconds` each,
   * dispatching them and leaving `depthTexture` pointing at the result.
   *
   * `substeps > 1` submits the whole run in ONE GPU submission and applies the
   * SAME `forcing` to every step, so it is valid ONLY for constant forcing.
   * Pass `substeps = 1` (the default) for a time-varying series — production
   * rainfall varies per substep and must not be batched.
   *
   * SYNCHRONOUS. Verified against installed three r180 rather than assumed:
   * `Renderer.compute()` (Renderer.js:2335) is fully synchronous once
   * `_initialized === true`; its only async branch is a fallback that warns
   * and defers to `computeAsync` when the backend has not been initialized.
   * `computeAsync` itself (Renderer.js:2434) is just
   * `if (!initialized) await init(); this.compute(...)` — it never waits for
   * GPU completion, and nothing in the compute path calls
   * `onSubmittedWorkDone`. So once the caller has awaited
   * `webgpuRenderer.init()` at setup, dispatching is a plain function call and
   * returning a Promise would buy a microtask per numerical substep for
   * nothing.
   *
   * Kernels are submitted in order to the same GPU queue, which is what
   * preserves step-to-step ordering — NOT the former `await`.
   */
  advance(
    webgpuRenderer: THREE.WebGPURenderer,
    dtSeconds: number,
    forcing: FloodForcing,
    substeps?: number,
  ): void

  /**
   * The canonical depth field as of the last `advance()`. Which resource backs
   * it is not the caller's concern.
   *
   * Retained as the primary API rather than being replaced by
   * `outputs.depth`: it is already stable, already used by every consumer, and
   * routing the hot path through a second indirection would be churn for its
   * own sake. `outputs.depth` returns the same texture.
   */
  readonly depthTexture: THREE.Texture

  /**
   * GPU fields this solver publishes. Read-only: consumers reference these
   * textures but never replace them, and the solver owns and disposes them —
   * the same ownership rule `depthTexture` already follows.
   *
   * For V1 this contains `depth` and nothing else, honestly: no analysis
   * textures are allocated, and no capability is advertised that isn't
   * implemented.
   */
  readonly outputs: FloodSolverOutputs

  /** Static hydraulic floor: conditioned terrain with buildings stamped. Read-only for consumers; the solver owns it. */
  readonly bedTexture: THREE.Texture

  /** Largest dtSeconds this instance can integrate explicitly without oscillating. */
  readonly maxStableDt: number

  /** Returns depth to the initial condition the solver was constructed with. */
  reset(): void

  /** Disposes solver-owned GPU resources. Consumers that merely reference `depthTexture` must NOT dispose it. */
  dispose(): void
}
